import {
  Alert,
  AlertIcon,
  Box,
  Button,
  ButtonProps,
  Flex,
  Icon,
  Link,
  ListItem,
  Popover,
  PopoverContent,
  PopoverProps,
  PopoverTrigger,
  SkeletonText,
  Text,
  UnorderedList,
  useDisclosure,
} from "@chakra-ui/react";
import { useFlags } from "flagsmith/react";
import React from "react";
import { RiPriceTag3Line } from "react-icons/ri";
import { useNavigate } from "react-router-dom";
import * as FSR from "src/generated/hooks/form-service/requests";
import { useConfirmationDialog } from "src/hooks/useConfirmationDialog";
import { useLazyRemoteDataQuery } from "src/hooks/useRemoteDataQuery";
import {
  GET_FORM_ENGINE_RULES_MAYBE_USING_TAG_ID,
  GET_TAG_USAGE_COUNT,
} from "src/scenes/orgAdmin/tags/graphql/queries";
import { OrgAdmin } from "src/services/url";
import * as GQL from "src/types/graphql";
import { WithId, WithoutId } from "src/types/id";
import { Tag, TagGroup } from "src/types/tags";
import { GenericError } from "../Feedback/GenericError";
import { Edit } from "./Edit";
import { New } from "./New";
import { SelectTagGroup } from "./SelectTagGroup";
import { TagsList, Props as TagsProps } from "./TagsList";
import { Mode } from "./types";

type FilteredRule = {
  id: string;
  form_template_id: string;
  enrollment_period_id: string;
  organization_path: string;
  name: string;
};

const DeleteTagConfirmationBody = ({
  rules,
  usageCount = 0,
  isLoading = true,
}: {
  rules?: FilteredRule[];
  usageCount?: number;
  isLoading?: boolean;
}): React.ReactNode => {
  const navigate = useNavigate();

  if (isLoading) {
    return (
      <Flex direction="column" gap="0.625rem">
        {/* Skeleton for usage count text */}
        <SkeletonText noOfLines={2} spacing="2" />
      </Flex>
    );
  }

  return (
    <Flex direction="column" gap="0.625rem">
      {(rules?.length ?? 0) > 0 && (
        <Alert status="warning" borderRadius="0.375rem">
          <AlertIcon />
          <Flex direction="column">
            <Text color={"orange.900"}>
              Tag is in use by a{" "}
              <Text color={"orange.900"} as="span" fontWeight="700">
                form rule to be applied automatically.
              </Text>{" "}
              Delete the rule first or remove the tag from it, and then try
              again:
            </Text>
            <UnorderedList pl="0.375rem">
              {rules
                ?.sort((lhs, rhs) =>
                  lhs.name.localeCompare(rhs.name, undefined, {
                    sensitivity: "base",
                  })
                )
                ?.map((r) => (
                  <ListItem
                    color={"orange.900"}
                    textDecoration={"underline"}
                    key={r.name}
                  >
                    <Link
                      href={OrgAdmin.FormTemplateRules.edit(
                        { path: r.organization_path },
                        r.enrollment_period_id,
                        r.form_template_id,
                        `ruleId=${r.id}&editRuleIsOpen=true`
                      )}
                      onClick={() => {
                        navigate(
                          OrgAdmin.FormTemplateRules.edit(
                            { path: r.organization_path },
                            r.enrollment_period_id,
                            r.form_template_id,
                            `ruleId=${r.id}&editRuleIsOpen=true`
                          )
                        );
                      }}
                    >
                      {r.name}
                    </Link>{" "}
                  </ListItem>
                ))}
            </UnorderedList>
          </Flex>
        </Alert>
      )}
      <Text>
        Deleting this tag will remove it from{" "}
        <Text as="span" fontWeight="700">
          {usageCount}
        </Text>{" "}
        records it was applied to.
      </Text>
    </Flex>
  );
};

const useDeleteTagConfirmationDialog = (): {
  confirm: (tag: Tag<WithId>) => Promise<boolean>;
  confirmationDialog: React.ReactNode;
} => {
  const flags = useFlags(["tags-form-service"]);
  const isFormServiceEnabled = flags["tags-form-service"].enabled;

  // Use lazy queries for GraphQL data
  const [fetchRules] = useLazyRemoteDataQuery<
    GQL.GetFormEngineRulesMaybeUsingTagID,
    GQL.GetFormEngineRulesMaybeUsingTagIDVariables
  >(GET_FORM_ENGINE_RULES_MAYBE_USING_TAG_ID, {
    fetchPolicy: "network-only",
  });

  const [getTagUsageCountGQL] = useLazyRemoteDataQuery<
    GQL.GetTagUsageCount,
    GQL.GetTagUsageCountVariables
  >(GET_TAG_USAGE_COUNT, {
    fetchPolicy: "network-only",
  });

  const { confirm, confirmationDialog, setBody, helpers } =
    useConfirmationDialog({
      header: `Delete tag?`,
      body: null,
      cancelButton: {
        label: "No, cancel",
      },
    });

  const confirmFn = async (tag: Tag<WithId>) => {
    // Show confirmation dialog with loading state
    const confirmPromise = confirm({
      header: `Delete "${tag.name}"?`,
      body: <DeleteTagConfirmationBody isLoading={true} />,
      confirmButton: {
        label: "Yes, delete",
        colorScheme: "primary",
        isLoading: true,
        isDisabled: true,
      },
    });

    // Load data in parallel while dialog is showing
    try {
      const [filteredRules, tagUsageCount] = await Promise.all([
        // Load rules data using lazy query
        fetchRules({
          variables: { tag_ids: [tag.id] },
        }).then(({ data, error }) => {
          if (error) throw error;

          const rules =
            data?.enrollment_period_tag
              .flatMap((tag) => tag.enrollment_period.form_templates)
              .flatMap((template) => template.form_engine_rules) ?? [];

          return rules
            .map((r) => {
              const cfg = JSON.parse(r.rule_config);
              return {
                id: r.id,
                form_template_id: r.form_template.id,
                enrollment_period_id: r.form_template.enrollment_period.id,
                organization_path:
                  r.form_template.enrollment_period.organization?.path ?? "",
                name: cfg["rule_name"] ?? "",
                tag_ids: cfg["tag_ids"] ?? [],
              };
            })
            .filter((r) => r.tag_ids.includes(tag.id));
        }),

        // Load usage count data
        isFormServiceEnabled
          ? // Use form service API directly
            FSR.getTagUsageCount({
              path: { id: tag.id },
            }).then((response) => {
              if (response.error) throw response.error;

              const result = response.data.data;
              return result.form_tag_count + result.form_school_tag_count;
            })
          : // Use GraphQL lazy query
            getTagUsageCountGQL({
              variables: { tag_id: tag.id },
            }).then(({ data, error }) => {
              if (error) throw error;

              return (
                (data?.form_tag_aggregate.aggregate?.count ?? 0) +
                (data?.form_school_tag_aggregate.aggregate?.count ?? 0)
              );
            }),
      ]);

      // Update the dialog content with loaded data
      setBody(
        <DeleteTagConfirmationBody
          usageCount={tagUsageCount}
          rules={filteredRules}
          isLoading={false}
        />
      );
      helpers.setButton({
        label: "Yes, delete",
        colorScheme: "primary",
        isLoading: false,
        isDisabled: filteredRules.length > 0,
      });
    } catch (error) {
      console.error("Failed to load tag deletion data:", error);

      // Update dialog with error state
      setBody(<GenericError message="Something went wrong!" />);
      helpers.setButton({
        label: "Yes, delete",
        colorScheme: "primary",
        isLoading: false,
        isDisabled: true,
      });
    }

    // Wait for user's final decision
    return await confirmPromise;
  };

  return {
    confirm: confirmFn,
    confirmationDialog,
  };
};

export type Props = {
  buttonProps?: ButtonProps;
  mode?: Mode;
  onNewTagSaving?: (values: Tag<WithoutId>) => Promise<void>;
  onNewTagCancelling?: () => void;
  onEditTagSaving?: (tag: Tag<WithId>, onSucess?: () => void) => Promise<void>;
  onEditTagCancelling?: () => void;
  onNewTagGroupSaving?: (
    tagGroup: TagGroup<WithoutId>,
    onSuccess: () => void
  ) => Promise<void>;
  onEditTagGroupSaving?: (
    id: uuid,
    tagGroup: TagGroup<WithoutId>,
    onSuccess: () => void
  ) => Promise<void>;
  onDeleteTagGroupSaving?: (id: uuid, onSuccess: () => void) => Promise<void>;
  error?: string;
  clearError: () => void;
  onCloseManageTagGroupDialog?: () => void;
  isBulkAction?: boolean;
  tagGroups?: TagGroup<WithId>[];
  onBackClick?: () => void;
} & PopoverProps &
  TagsProps;

export const TagsPopover: React.FC<Props> = ({
  buttonProps,
  mode = { type: "List" },
  onNewTagCancelling,
  onNewTagSaving,
  onEditTagCancelling,
  onEditTagSaving,
  onDeleteTag,
  error,
  clearError,
  onOpen,
  onClose,
  onSelectTagGroup,
  onNewTagGroupSaving,
  onEditTagGroupSaving,
  onCloseManageTagGroupDialog,
  onDeleteTagGroupSaving,
  isBulkAction,
  tagGroups,
  onBackClick,
  ...props
}) => {
  const [closeOnBlur, setCloseOnBlur] = React.useState(true);
  const { isOpen, onOpen: setOpen, onClose: setClose } = useDisclosure();

  const { confirm, confirmationDialog } = useDeleteTagConfirmationDialog();

  const searchInputRef = React.createRef<HTMLInputElement>();
  const newTagInputRef = React.createRef<HTMLInputElement>();
  const editTagInputRef = React.createRef<HTMLInputElement>();
  const selectTagGroupInputRed = React.createRef<HTMLInputElement>();

  const onOpenWrapper = React.useCallback(() => {
    setOpen();
    if (onOpen) onOpen();
  }, [setOpen, onOpen]);

  const onCloseWrapper = React.useCallback(() => {
    setClose();
    if (onClose) onClose();
  }, [setClose, onClose]);

  const onToggleTagDescription = React.useCallback((state: boolean) => {
    setCloseOnBlur(state);
  }, []);

  const onDeleteTagFn = async (tag: Tag<WithId>) => {
    if (onDeleteTag && (await confirm(tag))) {
      onDeleteTag(tag);
    }
  };

  let content;
  let initialFocusRef;
  switch (mode.type) {
    case "List":
      content = (
        <TagsList
          searchInputRef={searchInputRef}
          onDeleteTag={onDeleteTagFn}
          onClose={onCloseWrapper}
          onEditTagSaving={onEditTagSaving}
          isBulkAction={isBulkAction}
          onBackClick={onBackClick}
          onToggleTagDescription={onToggleTagDescription}
          {...props}
        />
      );
      initialFocusRef = searchInputRef;
      break;

    case "Edit":
      content = (
        <Edit
          onCancelling={onEditTagCancelling}
          onSaving={onEditTagSaving}
          tag={mode.tag}
          inputRef={editTagInputRef}
          error={error}
          clearError={clearError}
        />
      );
      initialFocusRef = editTagInputRef;
      break;

    case "New":
      initialFocusRef = newTagInputRef;
      content = (
        <New
          onCancelling={onNewTagCancelling}
          onSaving={onNewTagSaving}
          initialName={mode.initialName}
          inputRef={newTagInputRef}
          error={error}
          clearError={clearError}
        />
      );
      break;
    case "SelectGroup":
      initialFocusRef = selectTagGroupInputRed;
      content = (
        <SelectTagGroup
          tagGroups={tagGroups ?? []}
          error={error}
          onSelectTagGroup={onSelectTagGroup}
          onNewTagGroupSaving={onNewTagGroupSaving}
          onEditTagGroupSaving={onEditTagGroupSaving}
          onDeleteTagGroupSaving={onDeleteTagGroupSaving}
          onCloseManageTagGroupDialog={onCloseManageTagGroupDialog}
        />
      );
      break;
    default:
      const _exhaustiveCheck = mode;
      content = _exhaustiveCheck;
      break;
  }

  return (
    <Box>
      {confirmationDialog}
      <Popover
        placement="bottom-end"
        closeOnBlur={closeOnBlur}
        onOpen={onOpenWrapper}
        onClose={onCloseWrapper}
        {...props}
        initialFocusRef={initialFocusRef}
      >
        <PopoverTrigger>
          <Button
            isActive={isOpen}
            leftIcon={<Icon as={RiPriceTag3Line} />}
            variant="banner"
            {...buttonProps}
          >
            Tag
          </Button>
        </PopoverTrigger>
        <PopoverContent maxH="60vh" overflowY="auto">
          {content}
        </PopoverContent>
      </Popover>
    </Box>
  );
};
